import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getWishList } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import updateGetWishListsParameter from "@insite/client-framework/Store/Components/AddToListModal/Handlers/UpdateGetWishListsParameter";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import ScheduleReminderModal, {
    ScheduleReminderModalStyles,
} from "@insite/content-library/Components/ScheduleReminderModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Husky Custom Code - Start */
import { getProductConfigStateByConfigId } from "@husky-hrc/Framework/Store/Data/ProductConfig/ProductConfigSelectors";
import ExtendedApplicationState from "@husky-hrc/Framework/Store/ExtendedApplicationState";
import Constants from "@husky-hrc/Utilities/Constants";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Info from "@insite/mobius/Icons/Info";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioPresentationProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
/* Husky Custom Code - End */

interface OwnProps {
    extendedStyles?: AddToListModalStyles;
}

/* Husky Custom Code - Start */
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: ExtendedApplicationState) => {
    const { isOpen, productInfos, productConfigId } = state.addToListModal;
    const productConfigState = getProductConfigStateByConfigId(state, productConfigId).value;
    return {
        session: state.context.session,
        modalIsOpen: isOpen,
        productInfos,
        getWishListsParameter: state.components.addToListModal.getWishListsParameter,
        wishListsDataView: getWishListsDataView(state, state.components.addToListModal.getWishListsParameter),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        productConfigId,
        productConfigState,
        myListsDetailsPageUrl: getPageLinkByPageType(state, "MyListsDetailsPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        homePageUrl: getHomePageUrl(state),
    };
};
/* Husky Custom Code - End */

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
};

export interface AddToListModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    signInMessageText?: TypographyProps;
    descriptionText?: TypographyPresentationProps; // Husky Custom Code
    listsSelect?: DynamicDropdownPresentationProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    scheduleReminderCheckbox?: CheckboxPresentationProps;
    scheduleReminderModal?: ScheduleReminderModalStyles;
    // Husky Custom Code
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    isCompleteRadioGroup?: RadioGroupPresentationProps;
    isCompleteRadioContainer?: InjectableCss;
    isCompleteRadio?: RadioPresentationProps;
    isCompleteLabelContainer?: InjectableCss;
    isCompleteText?: TypographyPresentationProps;
    isCompleteTooltip?: TooltipPresentationProps;
}

export const addToListModalStyles: AddToListModalStyles = {
    modal: {
        size: 350,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    /* Husky Custom Code - Start */
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    descriptionText: {
        variant: "p",
    },
    listsSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
            formInputWrapper: css`
                input {
                    cursor: pointer;
                }
            `,
            option: css`
                color: ${getColor("text.main")};
                background-color: ${getColor("danger.contrast")};
                &:hover {
                    color: ${getColor("text.main")};
                    background-color: #f2d6cf;
                }
            `,
        },
        iconProps: {
            color: "danger.main",
        },
    },
    /* Husky Custom Code - End */
    newListInput: {
        cssOverrides: {
            formField: css`
                margin-top: 15px;
            `,
        },
    },
    scheduleReminderCheckbox: {
        css: css`
            margin-top: 15px;
            align-items: flex-start;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            /* Husky Custom Code */
            display: flex;
            gap: 15px;
            justify-content: center;
        `,
    },
    cancelButton: {
        variant: "secondary",
        /* Husky Custom Code */
        css: css`
            flex: 1 1 0;
            max-width: 175px;
        `,
    },
    /* Husky Custom Code - Start */
    addButton: {
        variant: "secondary",
        css: css`
            flex: 1 1 0;
            max-width: 175px;
        `,
    },
    isCompleteRadioGroup: {
        css: css`
            margin-top: 20px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            input {
                                margin-left: 0px;
                            }
                        `,
                        null,
                        null,
                        css`
                            display: inline-block;
                            & > ${RadioStyle} {
                                display: inline-block;
                                margin-top: 5px;
                                margin-right: 20px;
                            }
                        `,
                    ],
                    "min",
                )}
            input:checked + label:after {
                background: ${getColor("danger.main")};
            }
            input:checked:not(:disabled) + label::before {
                background: ${getColor("danger.contrast")};
                border-color: ${getColor("danger.main")};
            }
        `,
    },
    isCompleteLabelContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    isCompleteText: {
        variant: "p",
    },
    isCompleteTooltip: {
        iconProps: {
            src: Info,
            color: "#b6b9bb",
        },
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
        },
        typographyProps: {
            weight: "normal",
        },
    },
    /* Husky Custom Code - End */
};

export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const AddToListModal = ({
    productInfos,
    session,
    modalIsOpen,
    getWishListsParameter,
    wishListsDataView,
    signInUrl,
    location,
    setAddToListModalIsOpen,
    addToWishList,
    updateGetWishListsParameter,
    loadWishLists,
    extendedStyles,
    // Husky Custom Code
    productConfigId,
    productConfigState,
    myListsDetailsPageUrl,
    cartPageUrl,
    history,
    homePageUrl,
}: Props) => {
    const [styles] = useState(() => mergeToNew(addToListModalStyles, extendedStyles));

    const toasterContext = useContext(ToasterContext);

    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const [selectedWishListId, setSelectedWishListId] = useState("new");
    const [newListName, setNewListName] = useState("");
    const [selectListError, setSelectListError] = useState<React.ReactNode>("");
    const [newListNameError, setNewListNameError] = useState<React.ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [inProgress, setInProgress] = useState(false);
    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduleReminderModalIsOpen, setScheduleReminderModalIsOpen] = useState(false);
    const [currentWishList, setCurrentWishList] = useState<WishListModel>();
    /* Husky Custom Code - Start */
    const [showIsComplete, setShowIsComplete] = useState(false);
    const [configurationIsComplete, setConfigurationIsComplete] = useState("false");

    const localModalStyles: ModalPresentationProps = {
        cssOverrides: {
            modalContent: css`
                overflow-y: visible;
            `,
        },
    };

    const modalStyles = mergeToNew(Constants.Styles.modalStyles.modal, localModalStyles);

    useEffect(() => {
        if (productConfigState && location.pathname !== cartPageUrl) {
            const selectedStep = productConfigState.steps.find(step => step.current);
            const onLastStep = selectedStep === productConfigState.steps.slice(-1).pop();
            setShowIsComplete(onLastStep);
        }
    }, [productConfigState]);
    /* Husky Custom Code - End */

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }

        const lastUpdatedListId = getCookie(LastUpdatedListIdCookieName) || "new";
        setSelectedWishListId(lastUpdatedListId);
    }, [modalIsOpen]);

    useEffect(() => {
        if (modalIsOpen && !wishListsDataView.value && !wishListsDataView.isLoading) {
            loadWishLists(getWishListsParameter);
        }
    }, [getWishListsParameter]);

    useEffect(() => {
        if (wishListsDataView.value) {
            let options: OptionObject[] = [{ optionText: translate("Create New List"), optionValue: "new" }];
            options = options.concat(
                wishListsDataView.value
                    .filter(o => !o.isAutogenerated)
                    .map(o => ({ optionText: o.name, optionValue: o.id })),
            );
            setOptions(options);
        }
    }, [wishListsDataView]);

    if (!productInfos) {
        return null;
    }

    const modalCloseHandler = () => {
        setNewListName("");
        setSelectListError("");
        setNewListNameError("");
        // Husky Removed - setInProgress
        setAddToListModalIsOpen({ modalIsOpen: false });
    };

    const listChangeHandler = (value?: string) => {
        setSelectedWishListId(value || "new");
        setSelectListError("");
    };

    const debouncedUpdateParameter = debounce((value: string) => {
        updateGetWishListsParameter({
            ...getWishListsParameter,
            query: value || undefined,
        });
    }, 200);

    const listsInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedUpdateParameter(event.currentTarget.value);
    };

    const newListInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewListName(event.target.value);
        if (event.target.value) {
            setNewListNameError("");
        }
    };

    const scheduleReminderChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsScheduled(!isScheduled);
    };

    const closeScheduleReminderModalHandler = () => {
        setScheduleReminderModalIsOpen(false);
        setIsScheduled(false);
        modalCloseHandler();
    };

    const addToListButtonClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setNewListNameError("");
        setSelectListError("");

        if (!selectedWishListId || !options.some(o => o.optionValue === selectedWishListId)) {
            setSelectListError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }

        const isNewList = selectedWishListId === "new";
        if (isNewList && !newListName) {
            setNewListNameError(siteMessage("Lists_Enter_New_Wishlist_Name"));
            return;
        }
        setInProgress(true);

        /* Husky Custom Code - Start */
        const onCartPage = cartPageUrl?.toLowerCase() === location.pathname.toLowerCase();
        const isConfigurationComplete = onCartPage ? true : configurationIsComplete === "true";
        /* Husky Custom Code - End */

        addToWishList({
            productInfos,
            selectedWishList: isNewList ? undefined : wishListsDataView.value?.find(o => o.id === selectedWishListId),
            newListName,
            configId: productConfigId, // Husky Custom Code
            isComplete: isConfigurationComplete, // Husky Custom Code
            onSuccess: async (wishList: WishListModel) => {
                setCookie(LastUpdatedListIdCookieName, wishList.id);
                setSelectedWishListId(wishList.id);
                toasterContext.addToast({
                    body: siteMessage("Lists_ProductAdded"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                if (isScheduled) {
                    const updatedWishList = await getWishList({ wishListId: wishList.id, expand: ["schedule"] });
                    setCurrentWishList(updatedWishList);
                    setScheduleReminderModalIsOpen(true);
                } else {
                    modalCloseHandler();
                    history.push(homePageUrl);
                }
            },
            onError: (errorMessage: React.SetStateAction<React.ReactNode> /* Husky Custom Code */) => {
                setInProgress(false);
                if (isNewList) {
                    setNewListNameError(errorMessage);
                } else {
                    modalCloseHandler();
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                }
            },
            onComplete(wishListProps: { result: { wishList: any; errorMessage: any } } /* Husky Custom Code */) {
                if (wishListProps?.result?.wishList) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(wishListProps?.result?.wishList);
                } else if (wishListProps?.result?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(wishListProps?.result?.errorMessage);
                }
            },
        } as any);
    };

    /* Husky Custom Code - Start */
    const handleChangeConfigurationIsComplete = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfigurationIsComplete(event.target.value);
    };

    const radioLabel = (
        <StyledWrapper {...styles.isCompleteLabelContainer}>
            <Typography {...styles.isCompleteText}>{translate("Is this configuration complete?")}</Typography>
            <Tooltip
                {...styles.isCompleteTooltip}
                text={translate(
                    "If this configuration is complete, you will be able to add this item directly to your cart later. If you would like to review this item before purchasing, please select no.",
                )}
            />
        </StyledWrapper>
    );
    /* Husky Custom Code - End */

    const returnUrl = location.pathname + location.search;

    return (
        /* Husky Custom Code - Start */
        <Modal
            {...modalStyles}
            headline={isAuthenticated ? translate("Add to List") : translate("Please Sign In")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            {inProgress ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.innerWrapper} data-test-selector="productAddToListModal">
                    {!isAuthenticated && (
                        <Typography
                            data-test-selector="productAddToListModal_requireSignIn"
                            {...styles.signInMessageText}
                        >
                            {siteMessage(
                                "Lists_Must_Sign_In_Spire",
                                `${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`,
                            )}
                        </Typography>
                    )}
                    {isAuthenticated && (
                        <>
                            <Typography {...styles.descriptionText}>
                                {translate("Add items to a new or existing list.")}
                            </Typography>
                            {showIsComplete && (
                                <>
                                    <RadioGroup
                                        {...styles.isCompleteRadioGroup}
                                        label={radioLabel}
                                        value={configurationIsComplete}
                                        onChangeHandler={handleChangeConfigurationIsComplete}
                                    >
                                        <Radio {...styles.isCompleteRadio} value="true">
                                            {translate("Yes")}
                                        </Radio>
                                        <Radio {...styles.isCompleteRadio} value="false">
                                            {translate("No")}
                                        </Radio>
                                    </RadioGroup>
                                </>
                            )}
                            <DynamicDropdown
                                label={translate("Select List")}
                                {...styles.listsSelect}
                                isLoading={wishListsDataView.isLoading}
                                onInputChange={listsInputChangeHandler}
                                onSelectionChange={listChangeHandler}
                                selected={selectedWishListId}
                                options={options}
                                error={selectListError}
                                data-test-selector="productAddToListModal_listSelect"
                            />
                            {selectedWishListId === "new" && (
                                <TextField
                                    label={translate("New List Name")}
                                    value={newListName}
                                    error={newListNameError}
                                    onChange={newListInputChangeHandler}
                                    {...styles.newListInput}
                                    placeholder={translate("Enter New List Name")} // Husky Custom Code
                                    data-test-selector="productAddToListModal_newName"
                                />
                            )}
                            <Checkbox
                                {...styles.scheduleReminderCheckbox}
                                checked={isScheduled}
                                onChange={scheduleReminderChangeHandler}
                            >
                                {siteMessage("Lists_Schedule_Reminders")}
                            </Checkbox>
                            <StyledWrapper {...styles.buttonsWrapper}>
                                {/* Husky Moved down - Cancel */}
                                <Button
                                    {...styles.addButton}
                                    onClick={e => addToListButtonClickHandler(e)}
                                    data-test-selector="productAddToListModal_addButton"
                                    disabled={inProgress}
                                >
                                    {translate("Add to List")}
                                </Button>
                                <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                                    {translate("Cancel")}
                                </Button>
                            </StyledWrapper>
                            {isScheduled && currentWishList && (
                                <ScheduleReminderModal
                                    wishList={currentWishList}
                                    isOpen={scheduleReminderModalIsOpen}
                                    handleClose={closeScheduleReminderModalHandler}
                                    extendedStyles={styles.scheduleReminderModal}
                                />
                            )}
                        </>
                    )}
                </StyledWrapper>
            )}
        </Modal>
        /* Husky Custom Code - End */
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(AddToListModal));
